import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import {
  IconDeviceDesktop,
  IconShieldLock,
  IconUser,
} from "@tabler/icons-react";

export const Route = createFileRoute("/dashboard/settings")({
  component: SettingsLayout,
  staticData: {
    title: "Settings",
  },
});

type NavItem = {
  label: string;
  to?: string;
  icon: typeof IconShieldLock;
  soon?: boolean;
};

// Account settings only: who you are and how you sign in. How a site runs
// its inspections is a different thing and lives under /dashboard/workspace.
// Security is the only section that exists today. Profile and Appearance are
// shown anyway, so the settings area reads as the design intends rather than
// as a one-item list — but they are inert and marked "Soon". An item that
// looks clickable and isn't would be worse than either showing or hiding it
// outright.
const NAV_GROUPS: { heading: string; items: NavItem[] }[] = [
  {
    heading: "Account",
    items: [
      { label: "Profile", icon: IconUser, soon: true },
      {
        label: "Security",
        to: "/dashboard/settings/security",
        icon: IconShieldLock,
      },
      { label: "Appearance", icon: IconDeviceDesktop, soon: true },
    ],
  },
];

function SettingsLayout() {
  return (
    <div className="p-4 lg:p-7">
      <header className="mb-8">
        <h1 className="font-serif text-4xl font-bold leading-[1.1] tracking-tight">
          Settings
        </h1>
        <p className="text-muted-foreground mt-2 text-[15px]">
          Your account and how you sign in.
        </p>
      </header>

      <div className="border-border flex flex-col gap-8 border-t pt-8 md:flex-row md:gap-12">
        <nav
          aria-label="Settings sections"
          className="flex shrink-0 flex-col gap-6 md:w-52"
        >
          {NAV_GROUPS.map((group) => (
            <div key={group.heading} className="flex flex-col gap-1">
              <p className="text-muted-foreground mb-1 text-[11px] font-semibold uppercase tracking-[0.14em]">
                {group.heading}
              </p>
              {group.items.map((item) => {
                const Icon = item.icon;
                if (!item.to) {
                  return (
                    <span
                      key={item.label}
                      aria-disabled="true"
                      className="text-muted-foreground/70 flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm"
                    >
                      <Icon className="size-4 shrink-0" aria-hidden="true" />
                      {item.label}
                      {item.soon && (
                        <span className="text-muted-foreground/70 ml-auto text-[11px]">
                          Soon
                        </span>
                      )}
                    </span>
                  );
                }
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors"
                    activeProps={{
                      className: "bg-muted text-foreground font-medium",
                    }}
                  >
                    <Icon className="size-4 shrink-0" aria-hidden="true" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
